import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useInvoicePay,
  useInvoicePayMock,
  useExpiresCountdown,
  useCopyFeedback,
} from '@features/invoice-pay';
import type { UseInvoicePayResult } from '@features/invoice-pay';
import type { UseInvoicePayMockResult } from '@features/invoice-pay';
import { buildPublicUrl } from '@shared/config/env';
import { useToast } from '@shared/ui';
import { cn } from '@shared/lib/utils';
import { InvoicePayCardA } from './invoice-pay-card-a';
import { InvoicePayCardB, InvoiceSupportLine } from './invoice-pay-card-b';
import { InvoicePaySkeleton } from './invoice-pay-skeleton';
import { InvoicePayMockPanel } from './invoice-pay-mock-panel';

interface InvoicePayWidgetProps {
  invoiceId: string;
  /** Enabled via `?testing009` — mock all invoice UI states without API. */
  testingMode?: boolean;
}

function usePayController(
  invoiceId: string,
  testingMode: boolean
): UseInvoicePayResult | UseInvoicePayMockResult {
  const live = useInvoicePay(testingMode ? undefined : invoiceId);
  const mock = useInvoicePayMock(testingMode ? invoiceId : undefined);

  // Hooks must always run; pick the active branch.
  return testingMode ? mock : live;
}

export function InvoicePayWidget({ invoiceId, testingMode = false }: InvoicePayWidgetProps) {
  const { t } = useTranslation('invoice-pay');
  const { toast } = useToast();
  const [contentReady, setContentReady] = useState(false);

  const pay = usePayController(invoiceId, testingMode);
  const { copiedKey, copy } = useCopyFeedback(() => toast(t('copied'), 'success'));

  const countdown = useExpiresCountdown({
    expiresAt: pay.invoice?.expiresAt,
    serverTime: pay.invoice?.serverTime,
    onExpire: () => {
      void pay.onTimerExpire();
    },
  });

  const isInitialLoading =
    pay.uiState === 'LOADING' || (pay.isLoading && !pay.invoice && !pay.loadError);

  useEffect(() => {
    if (isInitialLoading) {
      setContentReady(false);
      return;
    }
    const id = window.requestAnimationFrame(() => setContentReady(true));
    return () => window.cancelAnimationFrame(id);
  }, [isInitialLoading]);

  useEffect(() => {
    if (pay.pollErrorStreak >= 3) {
      toast(t('connectionProblems'), 'error');
    }
  }, [pay.pollErrorStreak, t, toast]);

  const handleCurrencyChange = (code: string) => {
    pay.setSelectedCurrency(code);
    const networks =
      pay.invoice?.availableCurrencies.find((c) => c.code === code)?.networks ?? [];
    const nextNetwork = networks[0]?.code ?? '';
    pay.setSelectedNetwork(nextNetwork);
  };

  const handleNetworkChange = (code: string) => {
    pay.setSelectedNetwork(code);
  };

  const handleProceed = async () => {
    const ok = await pay.proceedToPayment();
    if (!ok) toast(t('failedToLoad'), 'error');
  };

  const handleForceCheck = async () => {
    const result = await pay.forceCheck();
    if (result === 'not_found') toast(t('transactionNotFound'), 'error');
    if (result === 'rate_limited') toast(t('checkCooldownHint'), 'info');
    if (result === 'error') toast(t('connectionProblems'), 'error');
  };

  const handleGoBack = () => {
    const url = pay.invoice?.returnUrl?.trim() || buildPublicUrl('/');
    window.location.assign(url);
  };

  const handleContactSupport = () => {
    const url = pay.invoice?.supportUrl ?? 'mailto:support@paygatex.io';
    window.location.assign(url);
  };

  const amount = pay.invoice?.amount ?? '—';
  const currency = pay.invoice?.currency ?? '';
  const mock = testingMode && 'isMock' in pay ? (pay as UseInvoicePayMockResult) : null;

  return (
    <>
      {isInitialLoading ? (
        <InvoicePaySkeleton />
      ) : (
        <div
          className={cn(
            'mx-auto w-full max-w-[540px] transition-opacity duration-normal ease-standard',
            contentReady ? 'opacity-100' : 'opacity-0'
          )}
        >
          <span className="sr-only" aria-live="polite">
            {pay.announceKey ? t(pay.announceKey) : ''}
          </span>

          <InvoicePayCardA
            amount={amount}
            currency={currency}
            amountUsd={pay.invoice?.amountUsd ?? null}
            networkCode={pay.invoice?.network ?? null}
            networkLabel={pay.invoice?.networkLabel ?? null}
            showNetworkFee={
              pay.uiState !== 'SUCCESS'
              && pay.uiState !== 'EXPIRED'
              && pay.uiState !== 'ERROR'
            }
            amountCopied={copiedKey === 'amount'}
            onCopyAmount={() => {
              void copy(pay.invoice?.amount ?? '', 'amount');
            }}
          />

          <InvoicePayCardB
            uiState={pay.uiState}
            invoice={pay.invoice}
            loadError={pay.loadError}
            isProceeding={pay.isProceeding}
            isChecking={pay.isChecking}
            checkCooldownLeft={pay.checkCooldownLeft}
            selectedCurrency={pay.selectedCurrency}
            selectedNetwork={pay.selectedNetwork}
            expiresFormatted={countdown.formatted}
            expiresUrgent={countdown.isUrgent}
            addressCopied={copiedKey === 'address'}
            txidCopied={copiedKey === 'txid'}
            idCopied={copiedKey === 'id'}
            onSelectCurrency={handleCurrencyChange}
            onSelectNetwork={handleNetworkChange}
            onProceed={() => void handleProceed()}
            onForceCheck={() => void handleForceCheck()}
            onCopyAddress={() => void copy(pay.invoice?.address ?? '', 'address')}
            onCopyTxid={() => void copy(pay.invoice?.txid ?? '', 'txid')}
            onCopyId={() => void copy(pay.invoice?.id ?? '', 'id')}
            onGoBack={handleGoBack}
            onRetry={() => void pay.reload()}
            onContactSupport={handleContactSupport}
          />

          <InvoiceSupportLine supportUrl={pay.invoice?.supportUrl ?? 'mailto:support@paygatex.io'} />
        </div>
      )}

      {mock ? (
        <InvoicePayMockPanel
          scenario={mock.mockScenario}
          onScenario={mock.setMockScenario}
          onToastDemo={() => toast(t('transactionNotFound'), 'error')}
        />
      ) : null}
    </>
  );
}
